import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Vehicle, VehicleFilters } from '../../../types/Vehicle';
import vehicleService from '../../../services/vehicleService';
import { VehicleListResponse } from '../../../types/Vehicle';
import VehiclesTable from '../components/VehiclesTable';
import VehicleFiltersComponent, { EMPTY_VEHICLE_FILTERS } from '../components/VehicleFilters';
import ServerPagination from '../../../components/ServerPagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import { PageHeader, ListPage } from '@/components/ui';

/** sessionStorage page key for this page's remembered list state (V2-024). */
const LIST_KEY = 'vehicles';

const VehiclesView: React.FC = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  // Filters, search, sort and page are kept for the sign-in (V2-024): leaving the page and
  // coming back, or Back, restores them.
  const [savedPage, setSavedPage] = usePersistedListState(LIST_KEY, 'page', { page: 1, pageSize: 20 });
  const [pagination, setPagination] = useState({
    page: savedPage.page,
    pageSize: savedPage.pageSize,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  const [filters, setFilters] = usePersistedListState<VehicleFilters>(LIST_KEY, 'filters', EMPTY_VEHICLE_FILTERS);
  const [search, setSearch] = usePersistedListState(LIST_KEY, 'search', '');
  const [searchInput, setSearchInput] = useState(search);
  const [sort, setSort] = usePersistedListState<{ by: string | null; dir: 'asc' | 'desc' }>(LIST_KEY, 'sort', {
    by: null,
    dir: 'asc',
  });
  const sortBy = sort.by;
  const sortDir = sort.dir;
  const [filterOptions, setFilterOptions] = useState<{
    currentStatuses: string[];
    serviceAreas: { id: number; name: string; code?: string }[];
    vehicleClassifications: string[];
    currentYards: { id: number; name: string; code?: string }[];
  }>({
    currentStatuses: [],
    serviceAreas: [],
    vehicleClassifications: [],
    currentYards: []
  });
  const { hasPermission } = usePermissions();
  const canCreateVehicle = hasPermission('vehicle:create');

  useEffect(() => {
    setSavedPage({ page: pagination.page, pageSize: pagination.pageSize });
  }, [pagination.page, pagination.pageSize]);

  // One fetch per change of page, page size, filters, search or sort. (The response's
  // pagination writes the same page back; the key stops that refetching.)
  const fetchKey = JSON.stringify([pagination.page, pagination.pageSize, filters, search, sortBy, sortDir]);
  const lastFetchKey = useRef<string | null>(null);
  useEffect(() => {
    if (lastFetchKey.current === fetchKey) return;
    lastFetchKey.current = fetchKey;
    fetchVehicles();
  }, [fetchKey]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const options = await vehicleService.getVehicleFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response: VehicleListResponse = await vehicleService.getVehiclesPaginated(
        pagination.page,
        pagination.pageSize,
        { ...filters, search, ...(sortBy ? { sortBy, sortOrder: sortDir } : {}) }
      );
      setVehicles(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVehicle = (vehicle: Vehicle) => {
    navigate(`/vehicles/${vehicle.id}`);
  };

  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    setSort({ by: key, dir });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (newFilters: VehicleFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Enter (or the clear "x") in the header-bar search field: search within the filters.
  const handleSearch = (value: string) => {
    setSearchInput(value.trim());
    setSearch(value.trim());
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClear = () => {
    setFilters(EMPTY_VEHICLE_FILTERS);
    setSearchInput('');
    setSearch('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPagination(prev => ({ ...prev, page: 1, pageSize }));
  };

  return (
    <ListPage>
      <PageHeader
        title="Vehicles"
        actions={[
          {
            key: 'add',
            label: 'Add Vehicle',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canCreateVehicle,
            onClick: () => navigate('/vehicles/new'),
          },
        ]}
      />

      <VehicleFiltersComponent
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        onClear={handleClear}
        searchApplied={search !== ''}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: handleSearch,
          placeholder: 'Unit number or description',
          label: 'Search vehicles',
        }}
      />

      {/* The table stays mounted while loading, so the scroll box and pagination do not jump. */}
      <ListPage.Body aria-busy={loading}>
        <VehiclesTable
          vehicles={vehicles}
          loading={loading}
          onSelectVehicle={handleSelectVehicle}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
        />
      </ListPage.Body>

      <ServerPagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        className="tw-mt-2"
      />
    </ListPage>
  );
};

export default VehiclesView;
